import React from 'react';
import {
  abortableFetch,
  DetailContext,
  SnackbarContext,
  UserContext,
  PromptContext,
  usePrompts,
  DetailHandle,
  useEventCallback,
  AbortableFetch,
  FormTextArea,
} from '@eas/common-web';
import { EvidenceAPI, Messages, Permission } from '../../../enums';
import { useContext, useRef } from 'react';
import { Subject } from '../../../models';
import { unstable_batchedUpdates } from 'react-dom';
import * as Yup from 'yup';
import { HelpLabel } from '../../../components/help/help-label';
import { getErrorMessage } from '../../../utils/get-message';

/**
 * Api call
 * @param id
 */
function callApi(id: string, note?: string) {
  return abortableFetch(`${EvidenceAPI.SUBJECTS}/${id}/active`, {
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    method: 'DELETE',
    body: JSON.stringify({ note: note }),
  });
}

/**
 * Zneplatnění subjektu
 */
const PROMPT_KEY = 'DEACTIVATE';

export function useDeactivateDialog() {
  /**
   * Context stuff
   */
  const { onPersisted, source } = useContext<DetailHandle<Subject>>(
    DetailContext
  );
  const { showSnackbar } = useContext(SnackbarContext);
  const { testPrompt } = useContext(PromptContext);
  const { hasPermission } = useContext(UserContext);

  /**
   * Fetch ref.
   */
  const fetch = useRef<AbortableFetch | null>(null);

  /**
   * Dialog
   */
  usePrompts([
    {
      key: PROMPT_KEY,
      dialogTitle: 'Zneplatnění subjektu',
      dialogText: 'Skutečně chcete zneplatnit subjekt?',
      FormFields: function Body() {
        return (
          <FormTextArea
            name="note"
            label={
              <HelpLabel
                label="Poznámka"
                code="SUBJECT_DEACTIVATE_DIALOG_NOTE"
                required
              />
            }
          />
        );
      },
      formValidationSchema: Yup.object().shape({
        note: Yup.mixed().nullable().required('Povinné pole'),
      }),
      dialogWidth: 600,
    },
  ]);

  const handleDeactivate = useEventCallback(() => {
    testPrompt({
      key: PROMPT_KEY,
      submitCallback: async ({ note }: { note?: string }) => {
        try {
          source.setLoading(true);
          if (fetch.current !== null) {
            fetch.current.abort();
          }

          fetch.current = callApi(source.data!.id, note);
          await fetch.current.raw();

          unstable_batchedUpdates(() => {
            showSnackbar(...Messages.Subject.DEACTIVATE.SUCCESS);
            source.setLoading(false);
          });

          onPersisted(source.data!.id);
          await source.refresh();
        } catch (err) {
          source.setLoading(false);
          if (err.name !== 'AbortError') {
            const message = getErrorMessage(
              Messages.Subject.DEACTIVATE,
              err.code
            );

            showSnackbar(...message);

            throw err;
          }
          return undefined;
        }
      },
    });
  });

  const showDeactivateButton =
    hasPermission(Permission.Subject.SUBJECT_INVALIDATE) &&
    !!source.data?.active;

  return {
    handleDeactivate,
    showDeactivateButton,
  };
}
